import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import React, { useEffect, useRef, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import ImagerApi from "../../api/ImagerApi";
import PlateApi from "../../api/PlateApi";
import PlateTypeCaptureProfileApi from "../../api/PlateTypeCaptureProfileApi";
import config from "../../config";
import IconClearDropLocation from "../../Icons/Command/ClearDropLocation";
import IconCancelImaging from "../../Icons/TopCommandPanel/CancelImaging";
import IconCaptureEfi from "../../Icons/TopCommandPanel/CaptureEfi";
import IconClearAllDropLocation from "../../Icons/TopCommandPanel/ClearAllDrop";
import IconImagePlateNow from "../../Icons/TopCommandPanel/ImagePlateNow";
import IconMovePlateTo from "../../Icons/TopCommandPanel/MovePlateTo";
import IconMoveToImager from "../../Icons/TopCommandPanel/MoveToImager";
import IconMoveToLoadPort from "../../Icons/TopCommandPanel/MoveToLoadPort";
import IconMoveToStorage from "../../Icons/TopCommandPanel/MoveToStorage";
import { ELightPath } from "../../Model/CaptureProfile";
import { EDropLocationCacheStatus } from "../../Model/EDropLocationCacheStatus";
import { EModuleType } from "../../Model/EModuleType";
import { ENotificationType } from "../../Model/ENotificationType";
import { Module } from "../../Model/Module";
import { EImagingModuleStatus, ModuleState } from "../../Model/ModuleState";
import { CanMovePlate } from "../../Model/Plate";
import { PlateAddress } from "../../Model/PlateAddress";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { getOneImagerCapabilities } from "../../redux/reducers/ImagerCapabilitiesReducer";
import { getModules } from "../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { getPlateAddresses } from "../../redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import ImagerUtil from "../../Utility/ImagerUtil";
import LiveStreamHeaderButton from "../Common/LiveStreamHeaderButton";
import LayoutContent from "../LayoutContent";
import ImagePlateUsing from "../Plate/CommandPanelViews/ImagePlateUsing";
import { DisplayValue } from "../PlateTypeEditor/DefaultImagingSettingViews/DefaultImagingSettingComboBox";
import Breadcrumb from "./../Header/Breadcrumb";
import Header from "./../Header/Header";
import HeaderButton from "./../Header/HeaderButton";
import HeaderDropDownButton, { IconDisplayValue } from "./../Header/HeaderDropDownButton";
import HeaderSelector from "./../Header/HeaderSelector";
import ClearDropLocationWarning from "./ClearDropLocationWarning";
import ImagerManualControl from "./ImagerManualControl";
import ImagerNavSubMenu from "./ImagerNavSubMenu";
import LiveImage from "./LiveImage";
import { isAuthenticationEnable, isSupportModeEnabled } from "../../redux/reducers/AuthenticationSettingReducer";

const LOCATE_DROP = "LOCATE_DROP";
const AUTO_FOCUS = "AUTO_FOCUS";
const IMAGE_PLATE = "IMAGE_PLATE";
const SAVE_IMAGE = "SAVE_IMAGE";
const RETURN_PLATE = "RETURN_PLATE";
const MOVE_TO = "MOVE_TO";
const CANCEL_IMAGING = "CANCEL_IMAGING";
const CLEAR_DROP_LOCATION = "CLEAR_DROP_LOCATION";
const CAPTURE_LIVE = "CAPTURE_LIVE";
const LIVE_TOGGLE = "LIVE_TOGGLE";
const CAPTURE_LIVE_IMAGE = "CAPTURE_LIVE_IMAGE";
const CAPTURE_EFI = "CAPTURE_EFI";
const CLEAR_CURRENT_DROP_LOC = "CLEAR_CURRENT_DROP_LOC";
const CLEAR_ALL_DROP_LOC = "CLEAR_ALL_DROP_LOC";
const MOVE_TO_LOADPORT = "MOVE_TO_LOADPORT";
const MOVE_TO_STORAGE = "MOVE_TO_STORAGE";

function getImagerOptions(imagerModules: Module[]): DisplayValue[] {
  return imagerModules.map((module) => {
    return {
      value: module.identifier,
      display: module.name,
    };
  });
}

function getMoveToOptions(
  imagerModules: Module[],
  chamberModules: Module[],
  loadPortModules: Module[],
  selectedIdentifier: string,
  plateAdressOrigin: PlateAddress,
  canMoveByModuleIdentifier: { [index: string]: boolean },
  allowMoveToLoadPort: boolean
): IconDisplayValue<string>[] {
  let options = [];
  const chamberIdentifiers = chamberModules.map((module) => module.identifier);
  const loadPortIdentifiers = loadPortModules.map(
    (module) => module.identifier
  );

  imagerModules.forEach((imager) => {
    options.push({
      value: imager.identifier,
      display: imager.name,
      icon: <IconMoveToImager style={{ width: 30, height: 20 }} />,
      disabled: imager.identifier === selectedIdentifier ? true : false,
    });
  });

  options.push({
    value: MOVE_TO_STORAGE,
    display: chamberIdentifiers.includes(plateAdressOrigin?.moduleIdentifier)
      ? "Return to Storage"
      : "Storage",
    icon: <IconMoveToStorage style={{ width: 41, height: 34 }} />,
    disabled: !canMoveByModuleIdentifier[chamberIdentifiers[0]],
  });

  options.push({
    value: MOVE_TO_LOADPORT,
    display:
      plateAdressOrigin == null ||
        loadPortIdentifiers.includes(plateAdressOrigin?.moduleIdentifier)
        ? "Return to Load Port"
        : "Load Port",
    icon: <IconMoveToLoadPort style={{ width: 41, height: 34 }} />,
    disabled: !canMoveByModuleIdentifier[loadPortIdentifiers[0]] || !allowMoveToLoadPort,
  });

  return options;
}

type TParams = { imagerIdentifier: string };

function IsModuleStateEqual(prev: ModuleState, next: ModuleState): boolean {
  if (prev == null && next != null) {
    return false;
  }

  if (prev.imager?.plate?.plateId !== next.imager?.plate?.plateId) {
    return false;
  }

  if (prev.imager?.freerunCapture !== next.imager?.freerunCapture) {
    return false;
  }

  if (prev.imager?.state !== next.imager?.state) {
    return false;
  }

  if (prev.imager?.activeLightPath !== next.imager?.activeLightPath) {
    return false;
  }

  return true;
}

interface HubRefParams {
  hubConnection: HubConnection | null;
}

const startSignalRConnection = (connection: HubConnection) =>
  connection
    .start()
    .then(() => {
      console.info("EFI SignalR Connected");
    })
    .catch((err) =>
      sendNotification(
        ENotificationType.Error,
        "EFI SignalR Connection Error: " + err
      )
    );

function Imager({ match }: RouteComponentProps<TParams>) {
  const isAuthEnable: boolean = useTypedSelector((state) => isAuthenticationEnable(state));
  const isEnableSupportMode = useTypedSelector(state => isSupportModeEnabled(state));

  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const chamberModules = useTypedSelector((state) =>
    getModules(state, EModuleType.CHAMBER)
  );
  const loadPortModules = useTypedSelector((state) =>
    getModules(state, EModuleType.LOADPORT)
  );
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const imagerIdentifier =
    match.params.imagerIdentifier || imagerModules[0].identifier;
  const imagerCapabilities = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerIdentifier)
  );
  const imagerModuleState = useTypedSelector(
    (state) => getModuleState(state, imagerIdentifier),
    IsModuleStateEqual
  );
  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrModules[0].identifier).psr
  );
  const [disabledLightPaths, setDisabledLightPaths] = useState(
    imagerCapabilities.lightpathCollection.allIds
  );
  const hubRef = useRef<HubRefParams>({ hubConnection: null });

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  const imagerState = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
  );

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  if (psrState == null) {
    throw new Error("Psr state is null");
  }

  const isReferencePlate = imagerState.plate?.isReferencePlate ?? true;
  const allowMoveToLoadPort = !isReferencePlate || (isAuthEnable || isEnableSupportMode);

  const [enableCancelButton, setEnableCancelButton] = useState(
    imagerState.state === EImagingModuleStatus.Imaging
  );

  const [canMovePlateByModuleIdentifier, setCanMovePlateByModuleIdentifier] =
    useState({} as { [moduleIdentifier: string]: boolean });

  useEffect(() => {
    setCanMovePlateByModuleIdentifier({});
    if (imagerState?.plate == null) return;

    PlateApi.canMovePlate(imagerState.plate).then((arr) => {
      const result: { [index: string]: boolean } = arr.reduce(
        (map: { [index: string]: boolean }, obj: CanMovePlate) => (
          (map[obj.moduleIdentifier] = obj.canMove), map
        ),
        {}
      );
      setCanMovePlateByModuleIdentifier(result);
    });
  }, [imagerState?.plate]);

  useEffect(() => {
    setEnableCancelButton(imagerState.state === EImagingModuleStatus.Imaging);
  }, [imagerState.state]);

  useEffect(() => {
    const OnEfiCompleted = () => {
      history.push("/imager/efi/" + imagerIdentifier);
    };

    const urlConnection = config.SIGNALR_URL + "/push";
    const hubConnection = new HubConnectionBuilder()
      .withUrl(urlConnection)
      .build();
    hubRef.current.hubConnection = hubConnection;

    hubConnection.on("OnEfiCompleted", OnEfiCompleted);

    hubConnection.onclose(() => {
      console.log("SignalR connection lost");
      setTimeout(() => {
        console.log("SignalR connection restarted");
        if (hubConnection != null) {
          startSignalRConnection(hubConnection);
        }
      }, 5000);
    });

    startSignalRConnection(hubConnection);

    return () => {
      hubConnection?.stop();
    };
  }, [imagerIdentifier]);

  const plateAddress = useTypedSelector(
    (state) =>
      getPlateAddresses(state).filter(
        (pa) => pa.moduleIdentifier === imagerIdentifier
      )[0]
  );

  const plate = imagerState.plate;
  const plateAdressOrigin = useTypedSelector(
    (state) =>
      getPlateAddresses(state).filter(
        (pa) => pa.reservedByPlate?.barcode === plate?.barcode
      )[0]
  );
  const history = useHistory();
  const manualControlEnabled = !ImagerUtil.getIsOpticControlDisabled(
    imagerState,
    suiteState
  );

  const isPlateOnImager = ImagerUtil.getIsPlateOnImager(imagerState);

  const [showImgPlateOption, setShowImgPlateOption] = useState(false);
  const plateAdress: PlateAddress = {
    id: 0,
    moduleIdentifier: imagerModuleState.identifier,
    moduleName: imagerModuleState.name,
    moduleType: EModuleType.IMAGER,
    hotelNo: 0,
    slotNo: 0,
    usedByPlate: plate,
    reservedByPlate: undefined,
  };

  const [
    showClearCurrentDropOverrideWarning,
    setShowClearCurrentDropOverrideWarning,
  ] = useState(false);
  const [
    showClearPlateDropOverrideWarning,
    setShowClearPlateDropOverrideWarning,
  ] = useState(false);
  const [plateDropLocationCacheStatus, setPlateDropLocationCacheStatus] =
    useState(EDropLocationCacheStatus.NoDropLocationCacheExist);
  const [dropLocationCacheStatus, setDropLocationCacheStatus] = useState(
    EDropLocationCacheStatus.NoDropLocationCacheExist
  );

  const updateDropLocationCacheStatus = () => {
    if (plate != null) {
      ImagerApi.isDropLocationExist(
        plate.barcode,
        imagerState.wellNumber,
        imagerState.dropNumber
      ).then((dropLocationCacheExistOnCurrentDrop) => {
        setDropLocationCacheStatus(dropLocationCacheExistOnCurrentDrop);
        if (
          dropLocationCacheExistOnCurrentDrop ===
          EDropLocationCacheStatus.DropLocationCacheExistOnImagerAndLims
        ) {
          setPlateDropLocationCacheStatus(dropLocationCacheExistOnCurrentDrop);
        } else {
          ImagerApi.isDropLocationExist(plate.barcode).then(
            (dropLocationExistOnPlate) => {
              setPlateDropLocationCacheStatus(dropLocationExistOnPlate);
            }
          );
        }
      });
    }
  };

  useEffect(() => {
    updateDropLocationCacheStatus();
  }, [
    plate?.barcode,
    imagerState.wellNumber,
    imagerState.dropNumber,
    imagerState.state,
  ]);

  useEffect(() => {
    if (plate != null) {
      PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
        plate.plateType.id,
        imagerIdentifier
      ).then((plateTypeCaptureProfiles) => {
        let disabledLightPaths: ELightPath[] = [];
        for (const plateTypeCaptureProfile of plateTypeCaptureProfiles) {
          if (!plateTypeCaptureProfile.enabled) {
            disabledLightPaths.push(plateTypeCaptureProfile.lightpath);
          }
        }
        setDisabledLightPaths(disabledLightPaths);
      });
    } else {
      setDisabledLightPaths([]);
    }
  }, [plate]);

  const onActionButtonClicked = (buttonId: string) => {
    switch (buttonId) {
      case LIVE_TOGGLE:
        if (imagerState.freerunCapture) {
          ImagerApi.stopStreaming(imagerIdentifier);
        } else {
          ImagerApi.startStreaming(imagerIdentifier);
        }
        break;
      case LOCATE_DROP:
        ImagerApi.doLocateDrop(imagerIdentifier);
        break;
      case AUTO_FOCUS:
        ImagerApi.doAutoFocus(imagerIdentifier);
        break;
      case IMAGE_PLATE:
        setShowImgPlateOption(true);
        break;
      case SAVE_IMAGE:
        console.log("save image");
        ImagerApi.downloadLastCapturedImage(
          imagerIdentifier,
          `${imagerState.wellName}_D${imagerState.dropNumber}.jpg`
        );
        break;
      case RETURN_PLATE:
        let barcode = plate != null ? plate.barcode : null;
        if (barcode != null) {
          PlateApi.returnPlate(imagerIdentifier);
        }
        break;
      case CAPTURE_LIVE:
        if (!imagerState.freerunCapture) {
          ImagerApi.startStreaming(imagerIdentifier).then(() =>
            ImagerApi.stopStreaming(imagerIdentifier)
          );
        }
        break;
      case CAPTURE_LIVE_IMAGE:
        // TODO: add capture live image action.
        console.log("CAPTURE LIVE IMAGE");
        break;
      case CANCEL_IMAGING:
        ImagerApi.doCancelImaging(imagerIdentifier).then(() =>
          setEnableCancelButton(false)
        );
        break;
      case CAPTURE_EFI:
        ImagerApi.doEfi(imagerIdentifier);
        // history.push("/imager/efi/" + imagerIdentifier);
        break;
      case CLEAR_ALL_DROP_LOC:
        if (plate != null) {
          if (
            plateDropLocationCacheStatus ===
            EDropLocationCacheStatus.DropLocationCacheExistOnImagerAndLims
          ) {
            setShowClearPlateDropOverrideWarning(true);
            setShowClearCurrentDropOverrideWarning(false);
          } else {
            ImagerApi.clearDropLocation(plate.barcode, false).then(() => {
              setPlateDropLocationCacheStatus(
                EDropLocationCacheStatus.NoDropLocationCacheExist
              );
              setDropLocationCacheStatus(
                EDropLocationCacheStatus.NoDropLocationCacheExist
              );
            });
          }
        }
        break;
      case CLEAR_CURRENT_DROP_LOC:
        if (plate != null) {
          if (
            dropLocationCacheStatus ===
            EDropLocationCacheStatus.DropLocationCacheExistOnImagerAndLims
          ) {
            setShowClearPlateDropOverrideWarning(false);
            setShowClearCurrentDropOverrideWarning(true);
          } else {
            ImagerApi.clearDropLocation(
              plate.barcode,
              false,
              imagerState.wellNumber,
              imagerState.dropNumber
            ).then(() => updateDropLocationCacheStatus());
          }
        }
        break;
      default:
        break;
    }
  };

  const onSelectedImagerChanged = (identifier: string) => {
    history.push("/imager/live-image/" + identifier);
    // setImagerIdentifier(identifier);
  };

  const onClickMoveTo = (id: string) => {
    switch (id) {
      case MOVE_TO_STORAGE: {
        let barcode = plate != null ? plate.barcode : null;
        if (barcode != null) {
          PlateApi.movePlateToStorage([plateAddress]);
        }
        break;
      }
      case MOVE_TO_LOADPORT: {
        let barcode = plate != null ? plate.barcode : null;
        if (barcode != null) {
          PlateApi.movePlateToLoadPort([plateAddress]);
        }
        break;
      }

      default:
        break;
    }
  };

  const onImgPlateBack = () => {
    setShowImgPlateOption(false);
  };

  // const captureImageOptions = [
  //   {
  //     value: CAPTURE_LIVE_IMAGE,
  //     display: "Capture Live Image",
  //     icon: <IconCaptureLiveImage style={{ width: 41, height: 34 }} />,
  //     disabled: false,
  //   },
  //   {
  //     value: CAPTURE_EFI,
  //     display: "Capture EFI",
  //     icon: <IconCaptureEfi style={{ width: 41, height: 34 }} />,
  //     disabled: false,
  //   },
  // ];

  const dropLocationOptions = [
    {
      value: CLEAR_ALL_DROP_LOC,
      display: "Clear All Drop Locations",
      icon: <IconClearAllDropLocation style={{ width: 34, height: 28 }} />,
      disabled:
        plateDropLocationCacheStatus ===
        EDropLocationCacheStatus.NoDropLocationCacheExist,
    },
    {
      value: CLEAR_CURRENT_DROP_LOC,
      display: "Clear Current Drop Location",
      icon: <IconClearDropLocation style={{ width: 34, height: 34 }} />,
      disabled:
        dropLocationCacheStatus ===
        EDropLocationCacheStatus.NoDropLocationCacheExist,
    },
  ];

  // console.log("Imager Rendered");
  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Live Image",
                to: "/imager/live-image",
                disabled: false,
              },
            ]}
          />
          {imagerModules.length > 1 && (
            <HeaderSelector
              value={imagerIdentifier}
              onChange={(identifier) => onSelectedImagerChanged(identifier)}
              displayValues={getImagerOptions(imagerModules)}
              noArrow={false}
              disabled={imagerModules.length === 1}
            />
          )}

          {/* <HeaderSelector
            value={selectedLightpath.toString()}
            onChange={(lp) => onSelectedLightpathChanged(Number.parseInt(lp))}
            displayValues={getLightpathOptions(imagerCapabilities)}
            noArrow={false}
            disabled={!manualControlEnabled}
          /> */}

          <Header.RightSide>
            <LiveStreamHeaderButton
              freerunCapture={imagerState.freerunCapture}
              imagerIdentifier={imagerIdentifier}
              disabled={!manualControlEnabled}
            />

            <HeaderButton
              id={CAPTURE_EFI}
              label="Capture EFI"
              icon={<IconCaptureEfi />}
              onClick={onActionButtonClicked}
              disabled={!manualControlEnabled || plate == null}
            />
            <HeaderButton
              id={IMAGE_PLATE}
              label="Image Now"
              icon={<IconImagePlateNow />}
              onClick={onActionButtonClicked}
              disabled={!isPlateOnImager}
            />
            <HeaderButton
              id={CANCEL_IMAGING}
              label="Cancel Imaging"
              icon={<IconCancelImaging />}
              onClick={onActionButtonClicked}
              disabled={!enableCancelButton}
            />
            <HeaderDropDownButton
              id={CLEAR_DROP_LOCATION}
              label={"Clear Drop"}
              icon={<IconClearDropLocation fill="#FFFFFF" />}
              onChange={onActionButtonClicked}
              iconDisplayValues={dropLocationOptions}
              disabled={
                !manualControlEnabled ||
                plate == null ||
                plateDropLocationCacheStatus ===
                EDropLocationCacheStatus.NoDropLocationCacheExist
              }
              dropDownStyle={{
                left: "-40px",
              }}
            />
            {/* <HeaderDropDownButton
              id={CAPTURE_IMAGE}
              label={"Capture Image"}
              icon={<IconCaptureImage />}
              onChange={onActionButtonClicked}
              iconDisplayValues={captureImageOptions}
              disabled={!manualControlEnabled}
            /> */}
            <HeaderDropDownButton
              id={MOVE_TO}
              label={"Move To"}
              icon={<IconMovePlateTo />}
              onChange={onClickMoveTo}
              disabled={!manualControlEnabled || plate == null}
              iconDisplayValues={getMoveToOptions(
                imagerModules,
                chamberModules,
                loadPortModules,
                imagerIdentifier,
                plateAdressOrigin,
                canMovePlateByModuleIdentifier,
                allowMoveToLoadPort
              )}
              dropDownStyle={{
                left: "-130px",
              }}
            />
            {/* <HeaderButton
              id={SAVE_IMAGE}
              label="Save Image"
              icon={<IconSaveImage />}
              onClick={onActionButtonClicked}
              disabled={!manualControlEnabled}
            /> */}
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <LiveImage imagerIdentifier={imagerIdentifier} />
        {(showClearCurrentDropOverrideWarning ||
          showClearPlateDropOverrideWarning) && (
            <ClearDropLocationWarning
              plateBarcode={imagerState.plate?.barcode ?? ""}
              allDrop={showClearPlateDropOverrideWarning}
              wellNumber={imagerState.wellNumber}
              dropNumber={imagerState.dropNumber}
              onClose={(clearDropLocationOverride) => {
                setShowClearCurrentDropOverrideWarning(false);
                setShowClearPlateDropOverrideWarning(false);
                updateDropLocationCacheStatus();
              }}
            />
          )}
      </LayoutContent.Content>
      <LayoutContent.SubMenu>
        <ImagerNavSubMenu imagerIdentifier={imagerIdentifier} />
      </LayoutContent.SubMenu>
      <LayoutContent.CommandPanel>
        {!showImgPlateOption && (
          <ImagerManualControl
            wellLimitEnabled={true}
            imagerIdentifier={imagerIdentifier}
            disabledLightPaths={disabledLightPaths}
          />
        )}
        {showImgPlateOption && (
          <ImagePlateUsing
            plateAddresses={[plateAdress]}
            back={onImgPlateBack}
            close={onImgPlateBack}
            showImageDrop={true}
          />
        )}
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}

export default Imager;

//Imager.whyDidYouRender = true;
